import type { InterviewerConfig } from '../types';

interface InterviewerAvatarProps {
  interviewer?: InterviewerConfig;
  isSpeaking?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export default function InterviewerAvatar({
  interviewer,
  isSpeaking = false,
  size = 'large',
}: InterviewerAvatarProps) {

  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-48 h-48 md:w-64 md:h-64',
  };

  const ringSize = {
    small: 'w-20 h-20',
    medium: 'w-28 h-28',
    large: 'w-56 h-56 md:w-72 md:h-72',
  };

  return (
    <div className="relative flex flex-col items-center justify-center">
      {/* 正在说话指示器 - 放在头像上方 */}
      {isSpeaking && (
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-1">
            <span
              className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-bounce"
              style={{ animationDelay: '0s' }}
            />
            <span
              className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-bounce"
              style={{ animationDelay: '0.1s' }}
            />
            <span
              className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            />
          </div>
        </div>
      )}

      <div className="relative flex items-center justify-center">
        {/* 外圈光环动画 */}
        {isSpeaking && (
          <>
            <div
              className={`absolute ${ringSize[size]} rounded-full border-2 border-amber-500/30 animate-ping`}
              style={{ animationDuration: '1.5s' }}
            />
            <div
              className={`absolute ${ringSize[size]} rounded-full border border-yellow-500/20 animate-ping`}
              style={{ animationDuration: '1.5s', animationDelay: '0.3s' }}
            />
          </>
        )}

        {/* 静态光环 */}
        <div
          className={`absolute ${ringSize[size]} rounded-full bg-gradient-to-br from-amber-500/10 to-yellow-500/10 blur-xl`}
        />

        {/* 头像容器 */}
        <div
          className={`
            relative ${sizeClasses[size]} rounded-full overflow-hidden
            bg-gradient-to-br from-[#1E3A5F] to-[#0F172A]
            border-2 border-amber-500/30
            transition-all duration-300
            ${isSpeaking ? 'scale-105 border-amber-400/40' : ''}
          `}
        >
          {/* 头像图片 */}
          <img
            src={interviewer.avatar}
            alt={interviewer.name}
            className="w-full h-full object-cover"
          />

          {/* 说话时的声波效果 */}
          {isSpeaking && (
            <div className="absolute inset-0 flex items-center justify-center">
              {/* 声波圆环 */}
              <div className="absolute w-full h-full">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute inset-0 rounded-full border border-amber-400/35 animate-ripple"
                    style={{
                      animationDelay: `${i * 0.3}s`,
                      animationDuration: '1.5s',
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 面试官名字 - 放在头像下方 */}
      <div className="mt-6 text-center">
        <h2 className="text-lg font-medium text-white">
          {interviewer.name}
        </h2>
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes ripple {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }

        .animate-ripple {
          animation: ripple 1.5s ease-out infinite;
        }
      `}</style>
    </div>
  );
}
